import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelectedDate } from "../context/SelectedDateContext";
import Logo from "../components/Logo";
import { ReservationService } from "../services/ReservationService";
import { AppVariablesService } from "../services/AppVariablesService";
import { UserService } from "../services/UserService";
import { capitalizeFirstLetter } from "../helpers/capitalizeFirstLetter";

export default function Confirm() {
  const [availability, setAvailability] = useState({});
  const [nextMonthAvailability, setNextMonthAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNovember2025Calendar, setShowNovember2025Calendar] = useState(false);
  const [nextMonthReleaseDay, setNextMonthReleaseDay] = useState(24);
  const [nextMonthReleaseTime, setNextMonthReleaseTime] = useState("13:00");
  const [showNextMonth, setShowNextMonth] = useState(false);
  const context = useSelectedDate();
  const navigate = useNavigate();

  // Function to check if next month should be available based on configured day and time
  useEffect(() => {
    const today = new Date();
    const currentDay = today.getDate();
    const currentHour = today.getHours();
    const currentMinutes = today.getMinutes();

    // IMPORTANT: Convert to number to ensure proper comparison
    const releaseDayNumber = Number(nextMonthReleaseDay);

    console.log('=== Next Month Availability Check ===');
    console.log('Current Date/Time:', today.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }));
    console.log('Current Day:', currentDay, '(type:', typeof currentDay, ')');
    console.log('Release Day:', nextMonthReleaseDay, '(type:', typeof nextMonthReleaseDay, ') -> Number:', releaseDayNumber);
    console.log('Current Hour:', currentHour, 'Minutes:', currentMinutes);
    console.log('Release Time:', nextMonthReleaseTime);

    // Check if next month is November 2025 (unless showNovember2025Calendar is enabled)
    const nextMonthDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const isNextMonthNovember2025 = nextMonthDate.getFullYear() === 2025 && nextMonthDate.getMonth() === 10; // Month is 0-indexed, so 10 = November

    // Parse the configured release time
    const [releaseHour, releaseMinutes] = nextMonthReleaseTime.split(':').map(Number);

    let isNextMonthAvailable = false;

    // Check if we're past the release day
    if (currentDay > releaseDayNumber) {
      isNextMonthAvailable = true;
      console.log('✓ Next month available: Current day', currentDay, '>', releaseDayNumber);
    }
    // Check if we're on the release day and past the release time
    else if (currentDay === releaseDayNumber) {
      const currentTotalMinutes = currentHour * 60 + currentMinutes;
      const releaseTotalMinutes = releaseHour * 60 + releaseMinutes;
      isNextMonthAvailable = currentTotalMinutes >= releaseTotalMinutes;
      console.log('On release day. Current minutes:', currentTotalMinutes, 'vs Release minutes:', releaseTotalMinutes);
      console.log(isNextMonthAvailable ? '✓ Time has passed' : '✗ Time has NOT passed yet');
    } else {
      console.log('✗ Next month NOT available: Current day', currentDay, '<', releaseDayNumber);
    }

    // Don't show next month if it's November 2025 and showNovember2025Calendar is false
    const shouldShowNextMonth = isNextMonthAvailable && (!isNextMonthNovember2025 || showNovember2025Calendar);
    console.log('Is November 2025?', isNextMonthNovember2025, 'Show November Calendar?', showNovember2025Calendar);
    console.log('>>> FINAL DECISION - Show Next Month:', shouldShowNextMonth);
    console.log('=====================================');
    setShowNextMonth(shouldShowNextMonth);
  }, [nextMonthReleaseDay, nextMonthReleaseTime, showNovember2025Calendar]);

  useEffect(() => {
    const fetchAppVariables = async () => {
      try {
        const appVariablesService = new AppVariablesService();
        const response = await appVariablesService.find();
        if (response?.config) {
          if (response.config.showNovember2025Calendar !== undefined) {
            setShowNovember2025Calendar(response.config.showNovember2025Calendar);
          }
          if (response.config.nextMonthReleaseDay !== undefined) {
            // Ensure it's a number
            setNextMonthReleaseDay(Number(response.config.nextMonthReleaseDay));
          }
          if (response.config.nextMonthReleaseTime !== undefined) {
            setNextMonthReleaseTime(String(response.config.nextMonthReleaseTime));
          }
        }
      } catch (err) {
        console.error("Error fetching app variables:", err);
      }
    };
    fetchAppVariables();
  }, []);

  useEffect(() => {
    async function fetchAvailability() {
      try {
        const service = new ReservationService();
        const result = await service.getMonthlyAvailability({ returnSchoolInfo: true });
        setAvailability(result);

        // If next month is available based on configured release day/time, fetch next month's availability
        if (showNextMonth) {
          const today = new Date();
          const nextMonthDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
          const nextMonthYear = nextMonthDate.getFullYear();
          const nextMonth = nextMonthDate.getMonth() + 1; // API expects 1-12

          // Fetch next month's availability in a single request
          const nextMonthResult = await service.getMonthlyAvailability({
            returnSchoolInfo: true,
            month: nextMonth,
            year: nextMonthYear
          });

          setNextMonthAvailability(nextMonthResult || {});
        }
      } catch (error) {
        console.error("Erro ao buscar disponibilidade:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchAvailability();
  }, [showNextMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0-indexed
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Next month calculations
  const nextMonthDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const nextMonthYear = nextMonthDate.getFullYear();
  const nextMonth = nextMonthDate.getMonth();
  const daysInNextMonth = showNextMonth ? new Date(nextMonthYear, nextMonth + 1, 0).getDate() : 0;

  // Calculate the weekday of the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfMonth = new Date(year, month, 1);
  const emptySlots = firstDayOfMonth.getDay();

  const handleDayClick = (day, isNextMonth = false) => {
    const selectedYear = isNextMonth ? nextMonthYear : year;
    const selectedMonth = isNextMonth ? nextMonth : month;
    const selected = new Date(selectedYear, selectedMonth, day);
    context.setSelectedDate(selected);
    
    const availabilityData = isNextMonth ? nextMonthAvailability : availability;
    context.setAvailableSchools(availabilityData[day] || []);
    
    const formattedDate = selected.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    navigate(`/available-reservations?date=${formattedDate}`);
  };

  const daysOfWeekShort = ["D", "S", "T", "Q", "Q", "S", "S"];

  if (loading) {
    return (
      <div className="text-center p-5">
        <h2>Carregando...</h2>
      </div>
    );
  }

  return (
    <div className="container p-3">
      <div
        className="d-flex align-items-center justify-content-center position-relative mb-4"
        style={{ height: "125px" }}
      >
        <Link to="/">
          <div
            className="position-absolute start-0 fw-bold"
            style={{ marginLeft: "30px", marginTop: "-20px" }}
          >
            &lt; Menu
          </div>
        </Link>
        <Logo center className="mb-4" size="125px" />
      </div>

      <h4 className="text-center fw-bold mb-4">Escolha o Dia</h4>
      <h2 className="text-center fw-bold mb-4">
        {capitalizeFirstLetter(
          today.toLocaleDateString("pt-BR", { month: "long" })
        )}
        {showNextMonth && ` / ${capitalizeFirstLetter(
          nextMonthDate.toLocaleDateString("pt-BR", { month: "long" })
        )}`}
      </h2>

      {/* Letras dos dias da semana */}
      <div
        className="d-grid mb-2"
        style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: "0px" }}
      >
        {daysOfWeekShort.map((letter, idx) => (
          <div
            key={idx}
            className="text-center fw-bold"
            style={{
              height: "30px",
              boxSizing: "border-box",
            }}
          >
            {letter}
          </div>
        ))}
      </div>

      <div
        className="d-grid"
        style={{
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "0px",
        }}
      >
        {/* Espaços vazios antes do primeiro dia, simulando botões com borda */}
        {Array.from({ length: emptySlots }).map((_, i) => (
          <div
            key={`empty-${i}`}
            style={{
              height: "50px",
              border: "1px solid #000",
              boxSizing: "border-box",
            }}
          />
        ))}

        {/* Renderização dos dias do mês atual */}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;

          const hasAvailability =
            Array.isArray(availability[day]) && availability[day].length > 0;

          return (
            <button
              key={`current-${day}`}
              onClick={() => hasAvailability && handleDayClick(day, false)}
              style={{
                height: "50px",
                backgroundColor: hasAvailability ? "lightgreen" : "lightcoral",
                border: "1px solid #000",
                cursor: hasAvailability ? "pointer" : "not-allowed",
                fontWeight: "bold",
                boxSizing: "border-box",
                padding: 0,
              }}
            >
              {day}
            </button>
          );
        })}
        
        {/* Renderização dos dias do próximo mês quando aplicável */}
        {showNextMonth && (() => {
          // Calculate where current month ends in the grid
          const totalCurrentMonthSlots = emptySlots + daysInMonth;
          const endOfCurrentMonthColumn = (totalCurrentMonthSlots - 1) % 7;
          
          // Calculate empty slots needed to complete the current month's last row
          const emptySlotsAfterCurrentMonth = endOfCurrentMonthColumn === 6 ? 0 : (6 - endOfCurrentMonthColumn);
          
          // Get the weekday of the first day of next month (0 = Sunday, 1 = Monday, etc.)
          const firstDayOfNextMonth = new Date(nextMonthYear, nextMonth, 1);
          const nextMonthStartWeekday = firstDayOfNextMonth.getDay();
          
          // Calculate total empty slots needed before next month's day 1
          // If current month ends on Saturday (column 6), next row starts fresh
          // Otherwise, we need to fill the rest of current row, then add a new row if needed
          let totalEmptySlots = 0;
          if (endOfCurrentMonthColumn === 6) {
            // Current month ends on Saturday, so next month starts on a new row
            totalEmptySlots = nextMonthStartWeekday;
          } else {
            // Fill rest of current row first
            totalEmptySlots = emptySlotsAfterCurrentMonth + 1; // +1 to move to next position
            // Then check if we need more empty slots for the correct weekday alignment
            const positionAfterFilling = (totalCurrentMonthSlots + emptySlotsAfterCurrentMonth + 1) % 7;
            if (positionAfterFilling !== nextMonthStartWeekday) {
              // Add more empty slots to reach the correct weekday
              const additionalSlots = (nextMonthStartWeekday - positionAfterFilling + 7) % 7;
              totalEmptySlots += additionalSlots;
            }
          }
          
          const elements = [];
          
          // Add empty slots before next month starts
          for (let j = 0; j < totalEmptySlots; j++) {
            elements.push(
              <div
                key={`separator-${j}`}
                style={{
                  height: "50px",
                  border: "1px solid #000",
                  boxSizing: "border-box",
                  backgroundColor: "#f0f0f0",
                }}
              />
            );
          }
          
          // Add next month's days
          for (let day = 1; day <= daysInNextMonth; day++) {
            const hasAvailability =
              Array.isArray(nextMonthAvailability[day]) && nextMonthAvailability[day].length > 0;
            
            elements.push(
              <button
                key={`next-${day}`}
                onClick={() => hasAvailability && handleDayClick(day, true)}
                style={{
                  height: "50px",
                  backgroundColor: hasAvailability ? "lightgreen" : "lightcoral",
                  border: "1px solid #000",
                  cursor: hasAvailability ? "pointer" : "not-allowed",
                  fontWeight: "bold",
                  boxSizing: "border-box",
                  padding: 0,
                  opacity: 0.9,
                }}
              >
                {day}
              </button>
            );
          }
          
          return elements;
        })()}
      </div>
    </div>
  );
}
